import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useMyDevices } from '../../hooks/useMyDevices';
import { addUserDevice } from '../../services/userDevices';

interface TrackDeviceButtonProps {
  deviceId: string;
  className?: string;
}

export function TrackDeviceButton({ deviceId, className = '' }: TrackDeviceButtonProps) {
  const { isAuthenticated } = useAuth();
  const myDevices = useMyDevices();
  const queryClient = useQueryClient();

  const alreadyTracked = (myDevices.data ?? []).some((ud) => ud.deviceId === deviceId);

  const mutation = useMutation({
    mutationFn: () => addUserDevice({ deviceId, batteryHealthPct: 100, purchasedAt: new Date().toISOString() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'devices'] });
    },
  });

  if (!isAuthenticated) {
    return (
      <Link to="/login" className={`btn-secondary ${className}`}>
        Sign in to track this device
      </Link>
    );
  }

  if (alreadyTracked) {
    return (
      <Link to="/me/devices" className={`btn-secondary ${className}`}>
        Tracking this device — manage it →
      </Link>
    );
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="btn-primary"
      >
        {mutation.isPending ? 'Adding…' : 'Track this device'}
      </button>
      {mutation.isError && (
        <p className="text-xs text-accent-red mt-2">Couldn&apos;t add this device — try again.</p>
      )}
    </div>
  );
}
